// User and Auth types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  // NULL for super-admin and patient roles.
  organizationId: string | null;
  // Set of hospitals this user is authorized against. Empty for super-admin and patient;
  // single entry for single-hospital roles; multi-entry for org admins and multi-hospital doctors.
  hospitalIds: string[];
  permissions: string[];
  createdAt: Date;
  updatedAt: Date;
}

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  HOSPITAL_ADMIN = 'hospital_admin',
  DOCTOR = 'doctor',
  NURSE = 'nurse',
  RECEPTIONIST = 'receptionist',
  BILLING = 'billing',
  // Hospital-scoped warehouse/equipment/HR personas (CORA-242). Kept in sync
  // with the backend Role enum (apps/backend/src/domain/models/role.enum.ts).
  STOCK_MANAGER = 'stock_manager',
  BIOMEDICAL = 'biomedical',
  HR = 'hr',
  PATIENT = 'patient',
}

/**
 * Values the API accepts for `appointments.type`.
 *
 * The validating definition is `AppointmentType` in
 * `apps/backend/.../dto/appointments/appointment.dto.ts`; this copy exists so the shape
 * is describable from shared code and MUST be kept in step with it. These two drifted
 * once already — this enum carried `follow_up` (underscore) and a `checkup` value that
 * the API has never accepted, so anything written against it was rejected at the boundary.
 */
export enum AppointmentType {
  CONSULTATION = 'consultation',
  FOLLOW_UP = 'follow-up',
  EMERGENCY = 'emergency',
  PROCEDURE = 'procedure',
  TEST = 'test',
  VACCINATION = 'vaccination',
}

/**
 * Values the API accepts for `appointments.status`. Same contract as
 * {@link AppointmentType}: mirrors the DTO enum, which is what validation runs against.
 * The hyphens matter — this copy previously used `in_progress` / `no_show`.
 */
export enum AppointmentStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no-show',
}

// CORA-359 — how a visit is paid for, on the patient and on each appointment booking. Kept in step with
// apps/backend/src/domain/constants/insurance-type.constants.ts by the backend's dto/api-enum-parity.spec.ts.
export const INSURANCE_TYPE_VALUES = ['particular', 'insurance', 'sns'] as const;

export type InsuranceType = (typeof INSURANCE_TYPE_VALUES)[number];

/**
 * Queue-entry status as the API stores and returns it (`QueueStatus` in
 * `apps/backend/.../domain/models/queue-entry.model.ts`, whitelisted for HTTP callers
 * by `QUEUE_STATUS_VALUES` in the queue DTO).
 *
 * This copy previously described a different model entirely — `waiting` / `called` /
 * `in_progress` / `no_show` — which no layer has ever used. The frontend maps these
 * values down to its own coarse `'waiting' | 'in-progress' | 'completed'` and to the
 * finer `workflowStage`; neither of those vocabularies belongs here.
 *
 * `AUTO_EXPIRED` is set only by the sweep cron, never by an HTTP caller.
 */
export enum QueueStatus {
  CHECKED_IN = 'CHECKED_IN',
  AWAITING_TRIAGE = 'AWAITING_TRIAGE',
  IN_TRIAGE = 'IN_TRIAGE',
  AWAITING_DOCTOR = 'AWAITING_DOCTOR',
  AWAITING_NURSE = 'AWAITING_NURSE',
  IN_CONSULTATION = 'IN_CONSULTATION',
  NOT_PRESENT = 'NOT_PRESENT',
  CHECKED_OUT = 'CHECKED_OUT',
  CANCELLED = 'CANCELLED',
  AUTO_EXPIRED = 'AUTO_EXPIRED',
}

// Inventory unit-of-measure vocabulary (CORA-262 follow-up). An inventory item's
// `unit` is a controlled slug, not free text; the frontend renders each slug via
// i18n (`warehouse.units.<slug>`). Kept in sync with the backend domain constant
// apps/backend/src/domain/constants/inventory-unit.constants.ts (mirrors the
// UserRole ↔ backend Role enum convention).
export const INVENTORY_UNITS = ['unit', 'box', 'pack', 'vial', 'ampoule', 'sachet', 'dose', 'tablet', 'ml', 'mg', 'g', 'l'] as const;

export type InventoryUnit = (typeof INVENTORY_UNITS)[number];

// Warehouse vocabulary (CORA-348). Kept in step with the backend domain constants
// apps/backend/src/domain/constants/warehouse.constants.ts: the backend's
// dto/api-enum-parity.spec.ts fails when the values or their order drift. The
// frontend renders each value via i18n (`warehouse.warehouses.types.<value>`,
// `warehouse.warehouses.activities.<value>`).
export enum WarehouseType {
  GENERAL = 'general',
  PHARMACY = 'pharmacy',
  COLD_CHAIN = 'cold_chain',
  CONSUMABLES = 'consumables',
  CONSIGNMENT = 'consignment',
}

export const WAREHOUSE_TYPE_VALUES: readonly WarehouseType[] = Object.values(WarehouseType);

/** Activities whose stock consumption is routed to a warehouse; an unmapped activity uses the hospital default. */
export enum WarehouseActivity {
  VACCINATION = 'vaccination',
  DISPENSING = 'dispensing',
  PROCEDURES = 'procedures',
}

// Also the order of the entries GET /warehouse-activity-mappings returns.
export const WAREHOUSE_ACTIVITY_VALUES: readonly WarehouseActivity[] = Object.values(WarehouseActivity);

export const WAREHOUSE_CODE_MAX_LENGTH = 20;
export const WAREHOUSE_NAME_MAX_LENGTH = 100;

// The API trims and upper-cases a code before checking it against this rule.
export const WAREHOUSE_CODE_PATTERN = new RegExp(`^[A-Z0-9][A-Z0-9_-]{0,${WAREHOUSE_CODE_MAX_LENGTH - 1}}$`);

// Stock transfer vocabulary (CORA-351; partially_received from CORA-353). Kept in step with
// apps/backend/src/domain/constants/stock-transfer.constants.ts by the backend's
// dto/api-enum-parity.spec.ts. The frontend renders each status via i18n.
export enum StockTransferStatus {
  DRAFT = 'draft',
  IN_TRANSIT = 'in_transit',
  PARTIALLY_RECEIVED = 'partially_received',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export const STOCK_TRANSFER_STATUS_VALUES: readonly StockTransferStatus[] = Object.values(StockTransferStatus);

export const STOCK_TRANSFER_MAX_LINES = 100;
export const STOCK_TRANSFER_NOTES_MAX_LENGTH = 1000;

// CORA-190 — DOB sentinel written for ER provisional patients, whose intake
// captures only name + vitals while `patients.date_of_birth` is NOT NULL.
// Mirrors PROVISIONAL_PATIENT_DEFAULTS in the backend queue repository. UI must
// treat a DOB equal to this as "unknown" and render the em-dash placeholder —
// computing an age from it yields a nonsense figure (e.g. "126 years").
export const PROVISIONAL_DATE_OF_BIRTH = '1900-01-01';

// CORA-358 — preparation instructions on the exam-type and treatment catalogs. Kept equal to the backend domain
// constant in apps/backend/src/domain/constants/preparation-instructions.constants.ts by dto/api-enum-parity.spec.ts.
export const PREPARATION_INSTRUCTIONS_MAX_LENGTH = 2000;
